import LoaderButton from "@app/components/loader-button.tsx";
import PageTitle from "@app/components/page-title.tsx";
import useUserData from "@app/data-hooks/use-user-data.ts";
import { Box, TextField } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Delimiter, PasswordField } from "bi-lib";

interface SearchParams {
    uid?: string;
    token?: string;
}

interface FormProps {
    password: string;
    passwordConfirm: string;
}

export const Route = createFileRoute("/account/reset-password")({
    component: Component,
    validateSearch: (search: Record<string, unknown>): SearchParams => {
        return {
            uid: search.uid as string | undefined,
            token: search.token as string | undefined,
        };
    },
});

function Component() {
    const { uid, token } = Route.useSearch();

    const { resetPassword } = useUserData();

    const navigate = useNavigate();

    const form = useForm({
        defaultValues: {} as FormProps,
        onSubmit: async ({ value }) => {
            await resetPassword.mutateAsync({
                user_name: uid,
                reset_password_token: token,
                new_password: value.password,
            });
            void navigate({ to: "/account/login" });
        },
    });

    return (
        <>
            <PageTitle>Reset Password</PageTitle>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <Box maxWidth={"sm"} margin={"0 auto"} flexDirection={"column"} display={"flex"} gap={2}>
                    <TextField label={"Email"} type={"email"} value={uid} required disabled />
                    <form.Field
                        name={"password"}
                        validators={{
                            onChange: ({ value }) => {
                                const errors: string[] = [];

                                // Check length
                                const minLength = 8;
                                if (value.length < minLength) {
                                    errors.push(`Must be at least ${minLength} characters`);
                                }

                                // Check for lowercase
                                if (!/[a-z]/.test(value)) {
                                    errors.push("Must contain at least 1 lowercase character");
                                }

                                // Check for uppercase
                                if (!/[A-Z]/.test(value)) {
                                    errors.push("Must contain at least 1 uppercase character");
                                }

                                // Check for number
                                if (!/[0-9]/.test(value)) {
                                    errors.push("Must contain at least 1 number");
                                }

                                // Check for number
                                if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
                                    errors.push("Must contain at least 1 symbol");
                                }

                                return errors.length == 0 ? undefined : errors.join(Delimiter);
                            },
                        }}
                    >
                        {(field) => (
                            <PasswordField
                                label={"New Password"}
                                value={field.state.value}
                                onChange={field.handleChange}
                                required
                                error={field.state.meta.errors.length > 0}
                                helperText={field.state.meta.errors.join(Delimiter)}
                            />
                        )}
                    </form.Field>
                    <form.Field
                        name={"passwordConfirm"}
                        validators={{
                            onChangeListenTo: ["password"],
                            onChange: ({ value, fieldApi }) => {
                                if (value !== fieldApi.form.getFieldValue("password")) {
                                    return "Passwords do not match";
                                }
                                return undefined;
                            },
                        }}
                    >
                        {(field) => (
                            <PasswordField
                                label={"Confirm New Password"}
                                value={field.state.value}
                                onChange={field.handleChange}
                                required
                                error={field.state.meta.errors.length > 0}
                                helperText={field.state.meta.errors.join(Delimiter)}
                            />
                        )}
                    </form.Field>
                    <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                        {([canSubmit]) => (
                            <LoaderButton
                                isLoading={resetPassword.isPending}
                                variant={"contained"}
                                type={"submit"}
                                disabled={!canSubmit}
                            >
                                Save
                            </LoaderButton>
                        )}
                    </form.Subscribe>
                </Box>
            </form>
        </>
    );
}
